import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Plus, Trash2, Loader2, ImagePlus, X, Smartphone, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const empty = { name: "", tagline: "", image_url: "", model_id: "", order: "" };

export const FeaturedManager = () => {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [models, setModels] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api.get("/featured").then((r) => {
      setItems(r.data);
      setDrafts(Object.fromEntries(r.data.map((m) => [m.id, { name: m.name, tagline: m.tagline, image_url: m.image_url }])));
    }).catch(() => {});
  useEffect(() => {
    load();
    api.get("/catalog").then((r) => setModels(r.data)).catch(() => {});
  }, []);

  const setDraft = (id, key, val) => setDrafts((d) => ({ ...d, [id]: { ...d[id], [key]: val } }));

  const uploadTo = async (file, cb) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    cb(data.url);
  };

  const uploadNew = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { await uploadTo(file, (url) => setForm((f) => ({ ...f, image_url: url }))); }
    catch { toast.error("Şəkil yüklənmədi"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const add = async () => {
    if (!form.name.trim()) { toast.error("Ad tələb olunur"); return; }
    setSaving(true);
    try {
      await api.post("/admin/featured", {
        name: form.name, tagline: form.tagline, image_url: form.image_url || null,
        model_id: form.model_id || null, order: parseInt(form.order) || (items.length + 1),
      });
      setForm(empty);
      await load();
      toast.success("Əlavə edildi");
    } catch { toast.error("Xəta baş verdi"); }
    finally { setSaving(false); }
  };

  const saveCard = async (id) => {
    try {
      await api.put(`/admin/featured/${id}`, drafts[id]);
      await load();
      toast.success("Yeniləndi");
    } catch { toast.error("Xəta baş verdi"); }
  };

  const remove = async (id) => {
    await api.delete(`/admin/featured/${id}`);
    load();
    toast.success("Silindi");
  };

  return (
    <div className="space-y-6" data-testid="featured-manager">
      {/* add */}
      <div className="glass rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4 text-sm font-medium"><Plus className="w-4 h-4" /> Yeni kart əlavə et</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ad" data-testid="featured-name" className="bg-white/5 border-white/10" />
          <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Alt yazı" data-testid="featured-tagline" className="bg-white/5 border-white/10" />
          <Input value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} placeholder="Sıra" type="number" data-testid="featured-order" className="bg-white/5 border-white/10" />
          <Select value={form.model_id} onValueChange={(v) => setForm({ ...form, model_id: v })}>
            <SelectTrigger data-testid="featured-model" className="bg-white/5 border-white/10 text-neutral-300"><SelectValue placeholder="Model (istəyə görə)" /></SelectTrigger>
            <SelectContent className="bg-[#111] border-white/10 text-white max-h-72">
              {models.map((m) => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadNew} />
            {form.image_url ? (
              <div className="relative">
                <img src={fileUrl(form.image_url)} alt="" className="w-11 h-11 rounded-lg object-contain bg-white/5 border border-white/10" />
                <button onClick={() => setForm({ ...form, image_url: "" })} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-black border border-white/20 flex items-center justify-center"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()} data-testid="featured-image-upload"
                className="h-10 px-3 rounded-lg border border-white/10 text-xs text-neutral-400 hover:text-white hover:border-white/30 transition-colors flex items-center gap-1.5">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Şəkil
              </button>
            )}
            <button onClick={add} disabled={saving} data-testid="featured-add-btn"
              className="h-10 flex-1 rounded-lg bg-neutral-100 text-black text-sm font-medium disabled:opacity-50 hover:bg-white transition-colors flex items-center justify-center gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Əlavə et
            </button>
          </div>
        </div>
      </div>

      {/* editable list */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((m) => {
          const d = drafts[m.id] || {};
          return (
            <div key={m.id} className="glass rounded-2xl overflow-hidden" data-testid={`featured-row-${m.id}`}>
              <label className="relative h-36 bg-[#0d0d0d] flex items-center justify-center p-3 cursor-pointer group">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadTo(f, (url) => setDraft(m.id, "image_url", url)).catch(() => toast.error("Yüklənmədi")); }} />
                {d.image_url ? <img src={fileUrl(d.image_url)} alt={d.name} className="h-full object-contain" /> : <Smartphone className="w-8 h-8 text-neutral-600" />}
                <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs gap-1.5"><ImagePlus className="w-4 h-4" /> Şəkli dəyiş</span>
              </label>
              <div className="p-3 space-y-2">
                <Input value={d.name || ""} onChange={(e) => setDraft(m.id, "name", e.target.value)} placeholder="Ad" data-testid={`featured-edit-name-${m.id}`} className="bg-white/5 border-white/10 h-9" />
                <Input value={d.tagline || ""} onChange={(e) => setDraft(m.id, "tagline", e.target.value)} placeholder="Alt yazı" data-testid={`featured-edit-tagline-${m.id}`} className="bg-white/5 border-white/10 h-9 text-sm" />
                <div className="flex gap-2">
                  <button onClick={() => saveCard(m.id)} data-testid={`featured-save-${m.id}`}
                    className="flex-1 h-9 rounded-lg bg-neutral-100 text-black text-sm font-medium hover:bg-white transition-colors flex items-center justify-center gap-1.5"><Save className="w-4 h-4" /> Saxla</button>
                  <button onClick={() => remove(m.id)} data-testid={`featured-delete-${m.id}`}
                    className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
